import { useEffect, useRef, useState } from 'react'
import { CheckCircle2 } from 'lucide-react'
import { Button } from '@/components/ui'
import { usePanelControls } from './helpFeedbackContext'
import { CLOSE, CLOSING_SOON, KEEP_OPEN, START_NEW, receiptLine } from './helpFeedbackText'

/** How long the receipt stays before the panel closes itself. */
export const RECEIPT_CLOSE_MS = 5000

/**
 * The sent line (decision 16): the reference, then the panel closes after five seconds unless the
 * person is in it (pointer or focus inside the drawer) or chose Keep open. Focus is never moved here.
 */
export function Receipt({ reference }: { reference: string }) {
  const { close, startFresh } = usePanelControls()
  const root = useRef<HTMLDivElement>(null)
  const [kept, setKept] = useState(false)

  useEffect(() => {
    if (kept) return
    const panel = root.current?.closest<HTMLElement>('[data-slot="drawer-content"]') ?? null
    let timer: ReturnType<typeof setTimeout> | null = null
    let hovered = false
    const focused = () => !!panel && panel.contains(document.activeElement)
    const stop = () => {
      if (timer) clearTimeout(timer)
      timer = null
    }
    const arm = () => {
      stop()
      if (hovered || focused()) return
      timer = setTimeout(close, RECEIPT_CLOSE_MS)
    }
    const enter = () => {
      hovered = true
      stop()
    }
    const leave = () => {
      hovered = false
      arm()
    }
    // focusout fires before focus lands elsewhere: look again once it has.
    const focusOut = () => setTimeout(arm, 0)
    arm()
    panel?.addEventListener('pointerenter', enter)
    panel?.addEventListener('pointerleave', leave)
    panel?.addEventListener('focusin', stop)
    panel?.addEventListener('focusout', focusOut)
    return () => {
      stop()
      panel?.removeEventListener('pointerenter', enter)
      panel?.removeEventListener('pointerleave', leave)
      panel?.removeEventListener('focusin', stop)
      panel?.removeEventListener('focusout', focusOut)
    }
  }, [kept, close])

  return (
    <div ref={root} className="space-y-2 text-sm" data-testid="help-feedback-receipt">
      <p className="flex items-center gap-1.5 font-medium">
        <CheckCircle2 aria-hidden className="size-4 text-success" />
        {receiptLine(reference)}
      </p>
      {kept ? (
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={startFresh}>{START_NEW}</Button>
          <Button size="sm" variant="ghost" onClick={close}>{CLOSE}</Button>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted-foreground">{CLOSING_SOON}</span>
          <Button size="sm" variant="ghost" onClick={() => setKept(true)}>{KEEP_OPEN}</Button>
        </div>
      )}
    </div>
  )
}
